import React, { useState } from "react";

// Components
import Dropdown from "react-bootstrap/Dropdown";
import Nav from "react-bootstrap/Nav";
import NavDropdown from "react-bootstrap/NavDropdown";
import { Link } from "gatsby";
import { FaIcon } from "./fa-icon";
import { faSquareArrowUpRight } from "@fortawesome/pro-solid-svg-icons";

// Context
import { useBrand } from "../contexts/brand-context";

// Utils
import { trimText } from "../utils/strings";

// Hooks
import useWindowSize from "../hooks/use-window-size";

export default function SiteLink({
  id,
  link,
  i,
  isDropdown,
  isDropdownItem,
  prefix,
  isActive = false,
  includeTextToImage = true,
  accentColour = false,
  appendIcon = false,
  extraClasses,
  brandLogo = false,
  forceOpenInNewWindow = false,
  brandLink = false,
  isButton = false,
  customClickFunc = false,
  identifier = false,
  searchLink = false,
  searchLinkText = false,
  searchLinkSlug = false,
  searchLinkType = false,
}) {
  const { isMdMinusOne } = useWindowSize();
  const { brand } = useBrand();
  const isBrandLinkExternalUrl =
    brandLink && link?.externalUrl && link.externalUrl !== "";

  let url = "";
  let hasExternalUrlButSameWindow = false;
  let openLinkInNewWindow = false;

  if (!isButton) {
    url =
      link?.internalUrl && brandLink === false
        ? link.internalUrl
        : link?.internalUrl?.slug && link.internalUrl.slug !== "/" && brandLink
          ? "/" + link.internalUrl.slug + "/"
          : link?.externalUrl
            ? link.externalUrl
            : link.pageLink?.slug
              ? "/" + link.pageLink.slug + "/"
              : "/";

    const isLinkToResultsPage =
      link.pageLink?.__typename === "ContentfulResultsPage";
    if (isLinkToResultsPage && !link.externalUrl) {
      url = "/results" + url;
    }

    const isLinkToEventsPage =
      link.pageLink?.__typename === "ContentfulProEvent";
    if (isLinkToEventsPage && !link.externalUrl) {
      url = "/events" + url;
    }

    const pageLinkBrand = link.pageLink?.brand?.id ?? false;
    const brandMismatch = brand.id !== pageLinkBrand;

    //if page and not pro brand, then add brand slug as prefix e.g. /e/test-page
    if (
      brand?.isNotBaseBrand &&
      link.pageLink?.__typename === "ContentfulProPage" &&
      brand?.brandSlug &&
      !link?.externalUrl &&
      !brandMismatch
    ) {
      const stringsToRemove = ["watch-e", "latest-e"];

      stringsToRemove.forEach((string) => {
        if (url.includes(string)) {
          url = url.replace(string, string.replace("-e", ""));
        }
      });

      url = "/" + brand?.brandSlug + url;
    }

    try {
      url = encodeURI(url);
    } catch (error) {
      console.error("Invalid URL encoding:", url, error);
      url = "#"; // Fallback to prevent rendering issues
    }

    hasExternalUrlButSameWindow =
      !link.openLinkInNewWindow && link?.externalUrl && brandLink === false;
    openLinkInNewWindow =
      link.openLinkInNewWindow === true || forceOpenInNewWindow ? "_blank" : "";
  }

  let componentAs =
    openLinkInNewWindow ||
    hasExternalUrlButSameWindow ||
    forceOpenInNewWindow ||
    isBrandLinkExternalUrl
      ? "a"
      : isButton
        ? "button"
        : Link;

  const [hover, setHover] = useState(false);
  const [clicked, setClicked] = useState(false);
  const handleMouseEnter = () => setHover(true);
  const handleMouseClick = () => {
    setHover(true);
    setClicked(true);
    if (customClickFunc) {
      customClickFunc();
    }
  };
  const handleMouseLeave = () => {
    setHover(false);
    setClicked(false);
  };
  const classes = extraClasses ? " " + extraClasses : "";
  let attributes = {
    as: componentAs,
    className: `nav-link${classes}`,
    key: `${id}_${i}`,
    target: openLinkInNewWindow,
    style: {
      color: hover || isActive || clicked ? accentColour : "",
    },
    onMouseEnter: handleMouseEnter,
    onMouseLeave: handleMouseLeave,
    onClick: handleMouseClick,
  };

  if (identifier) {
    attributes.id = identifier;
  }

  if (searchLink) {
    url = searchLinkSlug;
  }

  if (componentAs !== "button") {
    const index = [componentAs === "a" ? "href" : "to"];
    attributes[index] = url;

    if (!url || url === "#") {
      console.warn(`Invalid URL for linkText: "${link.linkText}"`);
    }
  }

  if (prefix) {
    attributes.bsPrefix = prefix;
  }

  let linkText = link.linkText;
  if (
    includeTextToImage &&
    process.env.GATSBY_WEBSITE === "Pro" &&
    link.openLinkInNewWindow === true &&
    !isDropdown &&
    !brandLogo
  ) {
    linkText = (
      <>
        {linkText} <FaIcon icon={faSquareArrowUpRight} />
      </>
    );
  }

  if (includeTextToImage && !isDropdown && brandLogo) {
    linkText = (
      <>
        <span className="brand-logo">{brandLogo}</span>
        <span className="sr-only visually-hidden">
          {link.linkText ?? "Brand"} Logo
        </span>
      </>
    );
  }

  if (searchLink) {
    let characterCount = 63;

    if (isMdMinusOne) {
      characterCount = 200;
    }

    linkText = (
      <div className="search-link-text">
        {trimText(searchLinkText, characterCount)}
      </div>
    );

    if (searchLinkType) {
      const typeSearchLink = (
        <div
          className="search-link-type"
          style={{ color: accentColour ? accentColour : "#fdf202" }}
        >
          {searchLinkType === "post" ? "Article" : searchLinkType}
        </div>
      );

      linkText = (
        <>
          {typeSearchLink}
          {linkText}
        </>
      );
    }
  }

  if (appendIcon) {
    linkText = (
      <>
        {linkText}{" "}
        <FaIcon
          icon={appendIcon}
          style={{ fill: accentColour && !brandLink ? accentColour : null }}
        />
      </>
    );
  }

  // Debugging
  // useEffect(() => {
  //   if(isBrandLinkExternalUrl){
  //     console.log(`Rendering SiteLink for: "${link.linkText}"`, attributes);
  //   }
  // }, [attributes]);

  return isDropdown ? (
    <NavDropdown.Item {...attributes}>{linkText}</NavDropdown.Item>
  ) : isDropdownItem ? (
    <Dropdown.Item {...attributes}>{linkText}</Dropdown.Item>
  ) : (
    <Nav.Link {...attributes}>{linkText}</Nav.Link>
  );
}
